import { Suspense, useEffect, useRef, useState } from "react";
import Header from "./components/landing/Header";
import Footer from "./components/landing/Footer";
import WalletPanel from "./components/WalletPanel";
import ReferEarnPanel from "./components/ReferEarnPanel";
import NotificationsPanel from "./components/NotificationsPanel";
import FriendsPanel from "./components/FriendsPanel";
import ChallengeModal from "./components/ChallengeModal";
import OutgoingChallengeCard from "./components/OutgoingChallengeCard";
import PongReconnectCard from "./components/PongReconnectCard";
import MatchReconnectCard from "./components/MatchReconnectCard";
import FPSReconnectCard from "./components/FPSReconnectCard";
import { type ReconnectGame } from "./lib/matchReconnect";
import { useAuth } from "./hooks/useAuth";
import { ProfileProvider } from "./hooks/useProfile";
import { SOLANA_NETWORK } from "./contracts/solana/config";
import SolanaQuickPlayDaemon from "./components/SolanaQuickPlayDaemon";
import { friendsStore } from "./store/friendsStore";
import { captureReferralFromUrl } from "./lib/referral";
import { loadSkipFpsBriefing } from "./lib/fpsBriefing";
import { adminApi, type MatchCheckIn } from "./auth/api";
import { useIsFullscreen } from "./hooks/useIsFullscreen";
import { useDocumentMeta } from "./hooks/useDocumentMeta";
import { metaForView } from "./lib/seoMeta";
import Router from "./router/Router";
import type { AppView, SnakeEntry, TournamentMatchContext, CustomMatchContext } from "./router/Router";

/** Map an invite game id to its transient custom-match view. */
function customViewForGame(game: CustomMatchContext["game"]): AppView {
  return game === "fps"
    ? "custom-fps"
    : game === "pong"
      ? "custom-pong"
      : game === "math"
        ? "custom-math"
        : "custom-td";
}

/** Map a tournament bracket game id to its transient match view. */
function tournamentViewForGame(game: TournamentMatchContext["game"]): AppView {
  return game === "fps"
    ? "tournament-fps"
    : game === "pong"
      ? "tournament-pong"
      : game === "math"
        ? "tournament-math"
        : "tournament-td";
}

type WalletView = "wallet" | "deposit" | "withdraw";

/**
 * URL ↔ view mapping. Only routes that warrant a real path live here;
 * intermediate flow screens (fps-stake, fps-briefing, etc.) stay
 * in-memory because they're transient steps inside a session.
 *
 * Snake is a special case: `/snake` lands the user on the stake/server
 * picker (`snake-stake`). Once they actually enter a server we keep the
 * URL at `/snake` because the active game is still conceptually the
 * snake page — just a different in-page state.
 */
const VIEW_TO_PATH: Partial<Record<AppView, string>> = {
  dashboard: "/",
  docs: "/docs",
  pong: "/pong",
  "fps-stake": "/fps",
  fps: "/fps",
  "snake-stake": "/snake",
  snake: "/snake",
  "math-stake": "/math",
  math: "/math",
  leaderboard: "/leaderboard",
  tournaments: "/tournaments",
  profile: "/profile",
  stats: "/stats",
  status: "/status",
  settings: "/settings",
  admin: "/admin",
  legal: "/legal",
  terms: "/terms",
  privacy: "/privacy",
  "responsible-gaming": "/responsible-gaming",
  "restricted-regions": "/restricted-regions",
};

function readViewFromPath(): AppView {
  if (typeof window === "undefined") return "dashboard";
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  // `/snake` resolves to the stake picker — a direct visit hasn't yet
  // entered a server, so we always boot into snake-stake.
  if (path === "/snake") return "snake-stake";
  if (path === "/math") return "math-stake";
  // `/fps` resolves to the stake picker — a direct visit hasn't entered a
  // match yet (matchmaking happens inside the iframe once they confirm).
  if (path === "/fps") return "fps-stake";
  for (const [view, p] of Object.entries(VIEW_TO_PATH)) {
    if (p === path) return view as AppView;
  }
  return "dashboard";
}

export default function App() {
  const auth = useAuth();
  const isFullscreen = useIsFullscreen();
  const [currentView, setCurrentView] = useState<AppView>(() => readViewFromPath());
  // Per-route <title>/description/robots/canonical for search engines.
  useDocumentMeta(metaForView(currentView));
  const [wager, setWager] = useState<number | undefined>(undefined);
  // Set by the FPS reconnect card → relaunches the iframe straight into the
  // saved seat. Cleared whenever a fresh FPS match is entered.
  const [fpsReconnectToken, setFpsReconnectToken] = useState<string | null>(null);
  const [snakeEntry, setSnakeEntry] = useState<SnakeEntry | undefined>(undefined);
  // The bracket match the user is currently playing, and the tournament to
  // reopen when they return. tournamentMatch stays set through the game view;
  // returnTournamentId is cleared when they navigate to the list via the header.
  const [tournamentMatch, setTournamentMatch] = useState<TournamentMatchContext | null>(null);
  const [returnTournamentId, setReturnTournamentId] = useState<string | null>(null);
  // The active friend-vs-friend custom match (set while in a custom-* view).
  const [customMatch, setCustomMatch] = useState<CustomMatchContext | null>(null);
  // The friend being challenged — opens the ChallengeModal when non-null.
  const [challengeTarget, setChallengeTarget] = useState<
    { address: string; handle: string; avatarVersion: number } | null
  >(null);
  // customMatchIds we've already routed the sender into, so the poll that
  // surfaces an accepted outgoing invite doesn't re-enter on every tick.
  const routedCustomRef = useRef<Set<string>>(new Set());
  // Whether the signed-in wallet is on the ADMIN_WALLETS allowlist — gates the
  // Header "Admin" link + the dashboard view.
  const [isAdmin, setIsAdmin] = useState(false);
  const [walletView, setWalletView] = useState<WalletView | null>(null);
  const [referEarnOpen, setReferEarnOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [friendsOpen, setFriendsOpen] = useState(false);
  // When non-null, the profile view renders this friend's stats instead
  // of the logged-in user's. Cleared when the user navigates to "their"
  // profile via the header.
  const [viewedProfile, setViewedProfile] = useState<{ address: string; handle: string } | null>(
    null,
  );

  // First-paint side-effect: pull `?ref=<code>` out of the URL and stash
  // it in localStorage so useAuth can forward it on the first /auth/privy
  // exchange. Strips the param from the address bar afterwards.
  useEffect(() => {
    captureReferralFromUrl();
  }, []);

  // Keep the (real) friends store authenticated so it syncs friend requests
  // + the notifications badge while logged in, and clears on sign-out.
  useEffect(() => {
    friendsStore.setAuth(auth.apiToken, auth.walletAddress);
  }, [auth.apiToken, auth.walletAddress]);

  // Resolve admin access so the Header can show the dashboard link. A wallet
  // qualifies if it's a super-admin (ADMIN_WALLETS) OR holds at least one staff
  // permission — the per-tab gating happens inside the dashboard. Clears on
  // sign-out. (Server re-checks every protected route, so this is purely UI.)
  useEffect(() => {
    if (!auth.apiToken) {
      setIsAdmin(false);
      return;
    }
    let active = true;
    adminApi
      .me(auth.apiToken)
      .then((r) => active && setIsAdmin(r.isSuperAdmin || r.permissions.length > 0))
      .catch(() => active && setIsAdmin(false));
    return () => {
      active = false;
    };
  }, [auth.apiToken]);

  // Push the path to the URL bar whenever currentView changes to a
  // routed view. Browser back/forward updates `currentView` via the
  // `popstate` listener below. This keeps deep-links working
  // (hupper.fun/pong loads Pong directly) without adding react-router.
  useEffect(() => {
    const path = VIEW_TO_PATH[currentView];
    if (!path) return; // transient view — leave the URL alone
    if (window.location.pathname.replace(/\/+$/, "") !== path.replace(/\/+$/, "")) {
      window.history.pushState({ view: currentView }, "", path);
    }
  }, [currentView]);

  useEffect(() => {
    const onPop = () => setCurrentView(readViewFromPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const walletAddress = auth.walletAddress;

  const handlePlayGame = (game: AppView, selectedWager?: number) => {
    if (game === "fps") {
      setCurrentView("fps-stake");
    } else if (game === "tower-defense") {
      setCurrentView("td-stake");
    } else if (game === "snake") {
      setCurrentView("snake-stake");
    } else if (game === "math") {
      setCurrentView("math-stake");
    } else {
      setCurrentView(game);
    }

    if (selectedWager) setWager(selectedWager);
  };

  const handleHeaderAction = () => {
    if (auth.isLoggedIn) setWalletView("wallet");
    else auth.setShowAuthModal(true);
  };

  const handleHeaderDeposit = () => {
    if (auth.isLoggedIn) setWalletView("deposit");
    else auth.setShowAuthModal(true);
  };

  const handleNavigate = (view: "leaderboard" | "profile" | "tournaments" | "docs" | "stats" | "admin") => {
    if (view === "profile" && !auth.isLoggedIn) {
      auth.setShowAuthModal(true);
      return;
    }
    // Hitting "profile" from the header always lands on the logged-in
    // user's profile, never a friend's leftover view.
    if (view === "profile") setViewedProfile(null);
    // Header "Tournaments" always opens the list, not a leftover detail.
    if (view === "tournaments") setReturnTournamentId(null);
    setCurrentView(view);
  };

  // Tournament detail → route the user into their ready bracket match.
  const handleEnterTournamentMatch = (ctx: TournamentMatchContext) => {
    setTournamentMatch(ctx);
    setReturnTournamentId(ctx.tournamentId);
    setCurrentView(tournamentViewForGame(ctx.game));
  };

  // Back from a bracket match → reopen the tournament detail (it re-polls and
  // reflects whether the user advanced or was eliminated).
  const handleTournamentMatchEnd = () => {
    setTournamentMatch(null);
    setCurrentView("tournaments");
  };

  // Route into a private custom match (both players carry the same
  // customMatchId). Called for the recipient straight from accept, and for the
  // sender when their poll sees the invite flip to accepted (see below).
  const enterCustomMatch = (checkIn: MatchCheckIn) => {
    if (checkIn.customMatchId) routedCustomRef.current.add(checkIn.customMatchId);
    setCustomMatch({
      game: checkIn.game,
      stake: Number(checkIn.stakeAmount),
      customMatchId: checkIn.customMatchId,
      opponentWallet: checkIn.opponent,
    });
    setCurrentView(customViewForGame(checkIn.game));
  };

  const handleCustomMatchEnd = () => {
    setCustomMatch(null);
    setCurrentView("dashboard");
  };

  // Sender side: once the friend accepts, the next friends poll surfaces the
  // outgoing invite as "accepted" with its customMatchId — route the sender
  // into the same room. The ref guards against re-entering on every poll.
  useEffect(() => {
    return friendsStore.subscribe((s) => {
      const accepted = s.matchInvitesOut.find(
        (i) => i.status === "accepted" && i.customMatchId && !routedCustomRef.current.has(i.customMatchId),
      );
      if (!accepted?.customMatchId) return;
      enterCustomMatch({
        game: accepted.game,
        stakeAmount: String(accepted.stake),
        customMatchId: accepted.customMatchId,
        opponent: accepted.address,
      });
    });
    // enterCustomMatch only calls stable setState setters — safe to bind once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleViewFriendProfile = (address: string, handleName: string) => {
    setViewedProfile({ address, handle: handleName });
    setFriendsOpen(false);
    setCurrentView("profile");
  };

  const handleShowSettings = () => {
    if (!auth.isLoggedIn) {
      auth.setShowAuthModal(true);
      return;
    }
    setCurrentView("settings");
  };

  return (
    <ProfileProvider token={auth.apiToken} wallet={auth.walletAddress}>
    {/* Keep the Solana quick-play auto-sweep (wallet SOL → Vault) running for
        the whole session so match locks never hit an empty Vault. */}
    {SOLANA_NETWORK && auth.isLoggedIn && <SolanaQuickPlayDaemon walletAddress={auth.walletAddress} />}
    <div className="bg-[var(--color-canvas)] min-h-screen text-[var(--color-text-primary)] w-full h-full flex flex-col font-sans p-0 m-0">
      {/* NOTE: `overflow-x-hidden` was on this root previously; that promoted
          overflow-y to `auto` and created a new scrolling container, which
          breaks `position: sticky` on the Header. The clip is now isolated
          to a wrapper around the in-flow content below; modal panels stay
          at root since they're `position: fixed` and unaffected. */}
      {/* Header is hidden while a game owns the viewport (Pong / Snake
          enter the browser's fullscreen mode on user click; the chrome
          would otherwise clutter the top of the fullscreen rect). Esc
          exits fullscreen → fullscreenchange listener flips the hook
          back to false → Header reappears. */}
      {!isFullscreen && (
        <Header
          isLoggedIn={auth.isLoggedIn}
          isAdmin={isAdmin}
          walletAddress={auth.walletAddress}
          firebaseUser={auth.authUser}
          onShowAuthModal={handleHeaderAction}
          onShowDeposit={handleHeaderDeposit}
          onShowReferEarn={() =>
            auth.isLoggedIn ? setReferEarnOpen(true) : auth.setShowAuthModal(true)
          }
          onShowFriends={() =>
            auth.isLoggedIn ? setFriendsOpen(true) : auth.setShowAuthModal(true)
          }
          onShowNotifications={() => setNotificationsOpen(true)}
          onShowWallet={() =>
            auth.isLoggedIn ? setWalletView("wallet") : auth.setShowAuthModal(true)
          }
          onShowSettings={handleShowSettings}
          onSignOut={auth.handleSignOut}
          onLogoClick={() => setCurrentView("dashboard")}
          onNavigate={handleNavigate}
        />
      )}

      <div className="flex-1 flex flex-col overflow-x-hidden">
        {/* One Suspense boundary covers every code-split view (see Router.tsx).
            The fallback matches the app canvas so a chunk fetch shows no flash. */}
        <Suspense fallback={<div className="flex-1 min-h-screen bg-[var(--color-canvas)]" />}>
        <Router
        currentView={currentView}
        onPlayGame={handlePlayGame}
        onOpenTournaments={() => {
          setReturnTournamentId(null);
          setCurrentView("tournaments");
        }}
        onShowWallet={() =>
          auth.isLoggedIn ? setWalletView("wallet") : auth.setShowAuthModal(true)
        }
        onCancelStake={() => setCurrentView("dashboard")}
        onBackFromBriefing={() => setCurrentView("fps-stake")}
        onFindMatch={(amount) => {
          setWager(amount);
          if (loadSkipFpsBriefing()) {
            // "Don't show this briefing again" — same handoff as the
            // briefing's Enter Match.
            setFpsReconnectToken(null);
            setCurrentView("fps");
          } else {
            setCurrentView("fps-briefing");
          }
        }}
        onFindTDMatch={(amount) => {
          setWager(amount);
          setCurrentView("tower-defense");
        }}
        onEnterSnakeServer={(entry) => {
          setSnakeEntry(entry);
          setCurrentView("snake");
        }}
        onFindMathMatch={(amount) => {
          // No real matchmaking server yet — Math is single-player vs a
          // simulated opponent for now (mirrors the original handoff
          // bundle). Hand off to the game page immediately.
          setWager(amount);
          setCurrentView("math");
        }}
        onEnterMatch={() => {
          setFpsReconnectToken(null); // fresh match — not a card reconnect
          setCurrentView("fps");
        }}
        onBackToDashboard={() => setCurrentView("dashboard")}
        onOpenLegal={(view) => setCurrentView(view)}
        stake={wager}
        snakeEntry={snakeEntry}
        walletAddress={walletAddress}
        apiToken={auth.apiToken}
        fpsReconnectToken={fpsReconnectToken}
        userHandle={auth.authUser?.displayName ?? null}
        viewedProfile={viewedProfile}
        onClearViewedProfile={() => setViewedProfile(null)}
        onViewProfile={() => handleNavigate("profile")}
        onViewPlayerProfile={handleViewFriendProfile}
        onEnterTournamentMatch={handleEnterTournamentMatch}
        onTournamentMatchEnd={handleTournamentMatchEnd}
        tournamentMatch={tournamentMatch}
        returnTournamentId={returnTournamentId}
        customMatch={customMatch}
        onCustomMatchEnd={handleCustomMatchEnd}
        onChallenge={(target) => setChallengeTarget(target)}
      />
        </Suspense>

        {(currentView === "dashboard" || currentView === "docs") && (
          <Footer
            onShowStatus={() => setCurrentView("status")}
            onShowDocs={() => setCurrentView("docs")}
            onShowLegal={(view) => setCurrentView(view)}
          />
        )}
      </div>

      {walletView !== null && auth.isLoggedIn && (
        <WalletPanel
          walletAddress={auth.walletAddress}
          user={auth.authUser}
          initialView={walletView}
          onClose={() => setWalletView(null)}
          onSignOut={auth.handleSignOut}
        />
      )}

      {referEarnOpen && auth.isLoggedIn && (
        <ReferEarnPanel
          walletAddress={auth.walletAddress}
          apiToken={auth.apiToken}
          onClose={() => setReferEarnOpen(false)}
        />
      )}

      {notificationsOpen && (
        <NotificationsPanel
          onClose={() => setNotificationsOpen(false)}
          onAcceptInvite={enterCustomMatch}
          walletAddress={auth.walletAddress}
          onShowWallet={() =>
            auth.isLoggedIn ? setWalletView("wallet") : auth.setShowAuthModal(true)
          }
        />
      )}

      {friendsOpen && auth.isLoggedIn && (
        <FriendsPanel
          walletAddress={auth.walletAddress}
          handle={auth.authUser?.displayName ?? null}
          apiToken={auth.apiToken}
          onClose={() => setFriendsOpen(false)}
          onViewProfile={handleViewFriendProfile}
          onChallenge={(target) => {
            setFriendsOpen(false);
            setChallengeTarget(target);
          }}
        />
      )}

      {challengeTarget && auth.isLoggedIn && (
        <ChallengeModal
          target={challengeTarget}
          walletAddress={auth.walletAddress}
          onClose={() => setChallengeTarget(null)}
          onShowWallet={() => {
            setChallengeTarget(null);
            setWalletView("wallet");
          }}
        />
      )}

      {/* Floating "challenge sent" card(s): shows the sender a live countdown
          + cancel for each pending outgoing custom-match invite. Self-hides
          when none are pending (accepted → routed in, declined, or expired). */}
      <OutgoingChallengeCard />

      {/* Floating reconnect card. Reads localStorage; only renders if
          there's a live Pong match the current wallet can rejoin. */}
      <PongReconnectCard
        walletAddress={walletAddress}
        onReconnect={() => setCurrentView("pong")}
      />

      {/* TD + Math reconnect cards — same localStorage-driven pattern
          as Pong, just sharing one component. Stack bottom-right so
          multiple games' cards don't overlap. Hidden while the player is
          inside the TD/Math flow (stake → searching → in-match) so the
          card only ever appears once they're back on the main site. */}
      {currentView !== "tower-defense" && currentView !== "td-stake" && (
        <MatchReconnectCard
          game="td"
          walletAddress={walletAddress}
          onReconnect={(g: ReconnectGame) =>
            setCurrentView(g === "td" ? "tower-defense" : "math")
          }
          stackIndex={0}
        />
      )}
      {currentView !== "math" && currentView !== "math-stake" && (
        <MatchReconnectCard
          game="math"
          walletAddress={walletAddress}
          onReconnect={(g: ReconnectGame) =>
            setCurrentView(g === "td" ? "tower-defense" : "math")
          }
          stackIndex={1}
        />
      )}

      {/* FPS reconnect card — token comes from the PlayCanvas iframe via
          postMessage, persisted in localStorage. Clicking relaunches the
          iframe straight into the saved seat. Only shown once the player
          is back on the main site: hidden across the whole FPS flow
          (stake → briefing → in-match) so it never appears over the
          matchmaking/searching screen as if a match already existed. */}
      {currentView !== "fps" &&
        currentView !== "fps-stake" &&
        currentView !== "fps-briefing" && (
          <FPSReconnectCard
            walletAddress={walletAddress}
            onReconnect={(token) => {
              setFpsReconnectToken(token);
              setCurrentView("fps");
            }}
          />
        )}
    </div>
    </ProfileProvider>
  );
}
